import axios, { AxiosHeaders } from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import qs from 'qs'
import { SetFingerprintToken, GetHash, GetFingerprint } from './fingerprint'

// 定义响应代码枚举
export enum ResultEnum {
  SUCCESS = 200,
  OK = 0,
  S0_PREFIX = 'S0',
  TOKEN_INVALID = 401,
}

// 定义缓存键枚举
export const TOKEN_KEY = import.meta.env.VITE_APP_TOKEN_KEY || 'token'

// 读取API基础URL
const baseApiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api/v0'

const service: AxiosInstance = axios.create({
  baseURL: baseApiUrl, // 基础请求地址，来自环境变量
  timeout: 50000, // 请求超时时间设置为 50 秒
  headers: { 'Content-Type': 'application/json;charset=utf-8' }, // 请求头设置
  paramsSerializer: (params) => {
    return qs.stringify(params) // 序列化请求参数
  },
})

// 请求拦截器
service.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // 更新指纹
    SetFingerprintToken()
    if (!config.headers) {
      config.headers = new AxiosHeaders()
    }
    config.headers['Content-Type'] = 'application/json;charset=utf-8'

    // 获取本地存储中的访问令牌
    const accessToken = localStorage.getItem(TOKEN_KEY)
    if (accessToken) {
      // 确保token带有Bearer前缀
      config.headers.Authorization = accessToken.startsWith('Bearer ') ? accessToken : `Bearer ${accessToken}` // 如果有访问令牌，添加到请求头中
    }

    // 获取指纹并设置到请求头
    const fingerprint = await GetHash()
    if (fingerprint) {
      config.headers.Session = fingerprint
    }

    // 判断请求的 url 是否是完整链接
    if (/^https?:\/\//.test(config.url || '')) {
      config.baseURL = '' // 如果是完整链接，则不使用 baseURL
    }

    return config
  },
  (error: any) => {
    return Promise.reject(error) // 请求错误处理
  },
)

// 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse) => {
    // 检查配置的响应类型是否为二进制类型（'blob' 或 'arraybuffer'），如果是，直接返回响应对象
    if (response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer') {
      return response
    }

    // 处理响应数据
    const { code, data, msg, err } = response.data

    // 检查响应状态码
    if (typeof code === 'string' && code.startsWith(ResultEnum.S0_PREFIX)) {
      return data // 如果响应成功，返回数据部分
    }

    // 如果响应错误，优先检查err，然后再检查msg
    const errorMessage = err || msg || '系统出错'
    return Promise.reject(new Error(errorMessage))
  },
  (error: any) => {
    // 异常处理
    if (error.response?.data) {
      const { code, msg, err } = error.response.data
      if (code === ResultEnum.TOKEN_INVALID || error.response.status === 401) {
        // 如果令牌无效或响应状态为401，通知用户并重新登录

        // 清除用户token
        localStorage.removeItem(TOKEN_KEY)

        // 跳转到登录页面
        window.location.href = '/auth/login'
        return Promise.reject(new Error('未登录或登录已过期，请重新登录'))
      } else {
        // 其他错误，记录错误信息，优先使用err字段
        const errorMessage = err || msg || '系统出错'
      }
    }
    return Promise.reject(error.message) // 返回错误信息
  },
)

const request = {
  get<T = any>(url: string, config?: Partial<InternalAxiosRequestConfig>): Promise<T> {
    return service.get(url, config)
  },

  post<T = any>(url: string, data?: any, config?: Partial<InternalAxiosRequestConfig>): Promise<T> {
    return service.post(url, data, config)
  },

  put<T = any>(url: string, data?: any, config?: Partial<InternalAxiosRequestConfig>): Promise<T> {
    return service.put(url, data, config)
  },

  delete<T = any>(url: string, config?: Partial<InternalAxiosRequestConfig>): Promise<T> {
    return service.delete(url, config)
  },
}

export default request
